import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Colors, Icon, Typography, } from '@zapier/design-system';
import { useState, useEffect, useRef } from 'react';
import { BuilderBlocks } from '@builder.io/react';
import { css } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { useTracking } from '../../tracking';
import { MEDIA_MIN_WIDTH } from '../../constants/responsive';
import { innerText } from '../TabsSection/utils';
const Styles = labelStyles('IconTabSection', {
    header: (backgroundColor) => css({
        backgroundColor: Colors[backgroundColor],
        padding: '40px 0 0',
        [MEDIA_MIN_WIDTH.small]: {
            padding: '40px 0 0',
        },
        [MEDIA_MIN_WIDTH.large]: {
            padding: '60px 0 0',
        },
    }),
    headingContainer: css({
        maxWidth: '1170px',
        margin: '0 auto 40px',
        padding: '0 20px',
        [MEDIA_MIN_WIDTH.small]: {
            padding: '0 32px',
        },
        [MEDIA_MIN_WIDTH.large]: {
            marginBottom: '64px',
        },
    }),
    heading: (color) => css({
        fontFamily: Typography.pageHeader1.fontFamily,
        fontWeight: 600,
        fontSize: '40px',
        lineHeight: '40px',
        letterSpacing: '1.5px',
        color: color,
        [MEDIA_MIN_WIDTH.small]: {
            maxWidth: '400px',
        },
        [MEDIA_MIN_WIDTH.large]: {
            fontSize: '70px',
            lineHeight: '68px',
            maxWidth: '672px',
        },
    }),
    navContainer: css({
        boxSizing: 'border-box',
        '@media (min-width: 384px)': {
            padding: '0 8px',
        },
        [MEDIA_MIN_WIDTH.small]: {
            padding: '0 32px',
        },
    }),
    nav: css({
        maxWidth: '1170px',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        '@media (min-width: 384px)': {
            flexDirection: 'row',
        },
    }),
    tab: (isSelected, itemBackgroundColor) => css({
        flex: 1,
        height: '44px',
        padding: '12px 6px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '22px',
        color: Colors.neutral800,
        textDecoration: 'none',
        backgroundColor: isSelected
            ? Colors.neutral100
            : Colors[itemBackgroundColor],
        borderTop: `1px solid ${Colors.earth}`,
        '@media (min-width: 384px)': {
            borderRight: `1px solid ${Colors.earth}`,
            '&:first-of-type': {
                borderLeft: `1px solid ${Colors.earth}`,
                borderTopLeftRadius: '5px',
            },
            '&:last-of-type': {
                borderTopRightRadius: '5px',
            },
        },
        [MEDIA_MIN_WIDTH.small]: {
            height: '64px',
            gap: '8px',
            padding: '24px',
            fontSize: '16px',
        },
        [MEDIA_MIN_WIDTH.large]: {
            height: '72px',
            padding: '28px',
        },
    }),
    icon: css({
        display: 'flex',
        '& > span': {
            width: '20px !important',
            height: '20px !important',
            [MEDIA_MIN_WIDTH.small]: {
                width: '26px !important',
                height: '26px !important',
            },
        },
    }),
});
export function IconTabsSection(props) {
    const root = useRef(null);
    const { navItems, content } = props.tabs.reduce((result, tab, index) => {
        const id = tab.id || tab.text.toLowerCase().replace(/\s/g, '-');
        const href = `#${id}`;
        return {
            navItems: [
                ...result.navItems,
                {
                    href,
                    text: tab.text,
                    icon: tab.icon,
                    backgroundColor: tab.backgroundColor,
                },
            ],
            content: Object.assign(Object.assign({}, result.content), { [href]: (_jsx(BuilderBlocks, { blocks: tab.content, dataPath: `component.options.tabs.${index}.content`, parentElementId: props.builderBlock.id })) }),
        };
    }, { navItems: [], content: {} });
    const [selectedTabHref, setSelectedTabHref] = useState(navItems[0].href);
    const contentHref = typeof window !== 'undefined' && (content === null || content === void 0 ? void 0 : content[window.location.hash])
        ? window.location.hash
        : navItems[0].href;
    useEffect(() => {
        setSelectedTabHref(contentHref);
    }, [contentHref]);
    useEffect(() => {
        if (window.location.hash &&
            Object.prototype.hasOwnProperty.call(content, window.location.hash) &&
            root.current) {
            root.current.scrollIntoView();
        }
    }, [content]);
    const { emitUserInteractionEvent } = useTracking();
    const handleTabChange = (event, navItem) => {
        if (!props.changeUrlOnTabChange) {
            event.preventDefault();
        }
        setSelectedTabHref(navItem.href);
        emitUserInteractionEvent({
            event_action: 'click',
            interaction_name: 'tab_change',
            interaction_raw_text: innerText(navItem.text),
            option_selected: innerText(navItem.text),
        });
    };
    return (_jsxs("div", Object.assign({ ref: root }, { children: [_jsxs("div", Object.assign({ css: Styles.header(props.headingBackgroundColor) }, { children: [_jsx("div", Object.assign({ css: Styles.headingContainer }, { children: _jsx("h1", Object.assign({ css: Styles.heading(props.headingColor) }, { children: props.heading })) })), _jsx("div", Object.assign({ css: Styles.navContainer }, { children: _jsx("nav", Object.assign({ css: Styles.nav }, { children: navItems.map((item) => {
                                return (_jsxs("a", Object.assign({ href: item.href, css: Styles.tab(item.href === selectedTabHref, item.backgroundColor), onClick: (event) => handleTabChange(event, item) }, { children: [_jsx("span", Object.assign({ css: Styles.icon }, { children: _jsx(Icon, { name: item.icon }) })), _jsx("span", { children: item.text })] }), item.href));
                            }) })) }))] })), _jsx("div", Object.assign({ css: Styles.tabContent }, { children: content[selectedTabHref] }))] })));
}
//# sourceMappingURL=IconTabsSection.js.map